import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, Legend, AreaChart, Area, ReferenceLine, Cell, ScatterChart,
  Scatter, ZAxis,
} from 'recharts';
import {
  Cpu, Layers, ArrowRight, ArrowLeft, ChevronRight, TrendingDown, TrendingUp,
  BarChart3, Shield, CheckCircle, AlertTriangle, Info, Upload, Search,
  Users, Send, Clock, Globe, Package, Zap, Target, Activity, FileText,
  DollarSign, Percent, ArrowUpRight, ArrowDownRight, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const publicApi = axios.create({ baseURL: `${BACKEND_URL}/api` });

/* ───────── helpers ───────── */
const fmt = (n) => {
  if (n >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(1)}K`;
  return `$${n.toFixed(2)}`;
};
const pct = (n) => `${n.toFixed(1)}%`;


/* ───────── main component ───────── */
const CostSavingsHub = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState('part');
  const [analyzing, setAnalyzing] = useState(false);
  const [results, setResults] = useState(null);
  const [assignDialog, setAssignDialog] = useState(null);
  const [knownMPNs, setKnownMPNs] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Part input state
  const [partForm, setPartForm] = useState({
    mpn: '', currentPrice: '', quantity: '1000', incoterms: 'DDP',
    distributorType: 'authorized', leadTime: '8', countryConstraints: '',
    dateCodeValidity: '2years', alternateGrade: 'form-fit-function',
  });

  // BOM input state
  const [bomFile, setBomFile] = useState(null);
  const [bomResults, setBomResults] = useState(null);

  const updatePartForm = (key, val) => setPartForm((p) => ({ ...p, [key]: val }));

  // Fetch known MPNs for autocomplete
  useEffect(() => {
    publicApi.get('/cost-savings/known-mpns')
      .then(res => setKnownMPNs(res.data))
      .catch(() => {});
  }, []);

  // Filtered suggestions
  const filteredMPNs = useMemo(() => {
    if (!partForm.mpn || partForm.mpn.length < 2) return [];
    const q = partForm.mpn.toLowerCase();
    return knownMPNs.filter(m => m.mpn.toLowerCase().includes(q)).slice(0, 6);
  }, [partForm.mpn, knownMPNs]);

  /* ───── analyze via real API ───── */
  const handleAnalyzePart = useCallback(async () => {
    if (!partForm.mpn || !partForm.currentPrice) {
      toast.error('Please enter MPN and current price');
      return;
    }
    setAnalyzing(true);
    try {
      const { data } = await publicApi.post('/cost-savings/analyze-part', {
        mpn: partForm.mpn,
        current_price: parseFloat(partForm.currentPrice),
        quantity: parseInt(partForm.quantity) || 1000,
        incoterms: partForm.incoterms,
        distributor_type: partForm.distributorType,
        lead_time: parseInt(partForm.leadTime) || 8,
        country_constraints: partForm.countryConstraints,
        date_code_validity: partForm.dateCodeValidity,
        alternate_grade: partForm.alternateGrade,
      });
      setResults(data);
      if (data._note) {
        toast.info(data._note);
      }
    } catch (err) {
      toast.error('Analysis failed. Please try again.');
      console.error(err);
    } finally {
      setAnalyzing(false);
    }
  }, [partForm]);

  const handleAnalyzeBOM = useCallback(async () => {
    if (!bomFile) {
      toast.error('Please upload a BOM file');
      return;
    }
    setAnalyzing(true);
    // Parse CSV client-side and analyze each part
    try {
      const text = await bomFile.text();
      const lines = text.split('\n').filter(l => l.trim());
      if (lines.length < 2) {
        toast.error('BOM file must have a header row and at least one data row');
        setAnalyzing(false);
        return;
      }
      const header = lines[0].toLowerCase();
      const mpnCol = header.split(',').findIndex(h => h.trim().includes('mpn') || h.trim().includes('part'));
      const qtyCol = header.split(',').findIndex(h => h.trim().includes('qty') || h.trim().includes('quantity'));
      const priceCol = header.split(',').findIndex(h => h.trim().includes('price') || h.trim().includes('cost'));

      const components = [];
      for (let i = 1; i < lines.length; i++) {
        const cols = lines[i].split(',').map(c => c.trim().replace(/"/g, ''));
        const mpn = cols[mpnCol >= 0 ? mpnCol : 0] || '';
        const qty = parseInt(cols[qtyCol >= 0 ? qtyCol : 1]) || 1000;
        const price = parseFloat(cols[priceCol >= 0 ? priceCol : 2]) || 0;
        if (!mpn || !price) continue;

        try {
          const { data } = await publicApi.post('/cost-savings/analyze-part', {
            mpn, current_price: price, quantity: qty,
            distributor_type: 'both',
          });
          components.push({
            mpn: data.part.mpn,
            make: data.part.make,
            basePrice: data.basePrice,
            marketMedian: data.marketMedian,
            qty: data.qty,
            savings: data.totalSavings,
            percentile: data.percentile,
            holdBuy: data.holdBuy,
            confidence: data.confidence,
          });
        } catch {
          components.push({
            mpn, make: 'Unknown', basePrice: price, marketMedian: price,
            qty, savings: 0, percentile: 50, holdBuy: 'HOLD', confidence: 50,
          });
        }
      }
      setBomResults(components);
      toast.success(`Analyzed ${components.length} components`);
    } catch (err) {
      toast.error('Failed to parse BOM file');
    } finally {
      setAnalyzing(false);
    }
  }, [bomFile]);

  const handleAssign = (item) => setAssignDialog(item);
  const submitAssign = () => {
    toast.success('Action assigned to team member');
    setAssignDialog(null);
  };

  const bomTotalSavings = useMemo(() =>
    (bomResults || []).reduce((s, c) => s + c.savings, 0), [bomResults]);

  /* ───── sample data for demo ───── */
  const SAMPLE_PARTS = useMemo(() => [
    { mpn: 'STM32F407VGT6', price: 12.80, qty: 2000, label: 'ARM MCU', make: 'STMicroelectronics' },
    { mpn: 'ESP32-WROOM-32E', price: 5.40, qty: 5000, label: 'WiFi Module', make: 'Espressif' },
    { mpn: 'TPS54331DR', price: 2.50, qty: 3000, label: 'Power IC', make: 'Texas Instruments' },
    { mpn: 'GRM188R71C104KA01D', price: 0.018, qty: 50000, label: 'MLCC Cap', make: 'Murata' },
    { mpn: 'MAX232CPE', price: 11.00, qty: 1000, label: 'RS-232 Driver', make: 'Analog Devices' },
  ], []);

  const SAMPLE_BOM = useMemo(() => [
    { mpn: 'STM32F407VGT6', qty: 2000, price: 12.80 },
    { mpn: 'ESP32-WROOM-32E', qty: 5000, price: 5.40 },
    { mpn: 'TPS54331DR', qty: 3000, price: 2.50 },
    { mpn: 'GRM188R71C104KA01D', qty: 50000, price: 0.018 },
    { mpn: 'RC0402FR-071KL', qty: 100000, price: 0.015 },
    { mpn: 'BAT54S,215', qty: 10000, price: 0.18 },
    { mpn: 'USB3320C-EZK-TR', qty: 2000, price: 2.80 },
    { mpn: 'LAN8720A-CP-TR', qty: 3000, price: 1.35 },
    { mpn: 'LM358P', qty: 5000, price: 0.55 },
    { mpn: 'MAX232CPE', qty: 1000, price: 11.00 },
  ], []);

  const handleSelectSample = useCallback((sample) => {
    setPartForm(prev => ({
      ...prev,
      mpn: sample.mpn,
      currentPrice: String(sample.price),
      quantity: String(sample.qty),
    }));
  }, []);

  const handleSampleBOM = useCallback(async () => {
    setAnalyzing(true);
    const components = [];
    for (const item of SAMPLE_BOM) {
      try {
        const { data } = await publicApi.post('/cost-savings/analyze-part', {
          mpn: item.mpn,
          current_price: item.price,
          quantity: item.qty,
          distributor_type: 'both',
        });
        components.push({
          mpn: data.part.mpn,
          make: data.part.make,
          basePrice: data.basePrice,
          marketMedian: data.marketMedian,
          qty: data.qty,
          savings: data.totalSavings,
          percentile: data.percentile,
          holdBuy: data.holdBuy,
          confidence: data.confidence,
        });
      } catch {
        components.push({
          mpn: item.mpn, make: 'Unknown', basePrice: item.price,
          marketMedian: item.price, qty: item.qty, savings: 0,
          percentile: 50, holdBuy: 'HOLD', confidence: 50,
        });
      }
    }
    setBomResults(components);
    setAnalyzing(false);
    toast.success(`Sample BOM analyzed — ${components.length} components`);
  }, [SAMPLE_BOM]);


  /* ───── Render ───── */
  return (
    <div className="min-h-screen bg-gray-50" data-testid="cost-savings-hub">
      {/* Header */}
      <nav className="sticky top-0 z-50 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/')} className="flex items-center gap-1 text-gray-500 hover:text-gray-900 text-sm" data-testid="back-to-home">
              <ArrowLeft className="w-4 h-4" /> Home
            </button>
            <div className="h-5 w-px bg-gray-200" />
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-[#0B3D91] rounded flex items-center justify-center">
                <span className="text-white font-bold text-[10px]">A</span>
              </div>
              <span className="text-sm font-semibold text-gray-800">Cost Savings</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" className="border-gray-300 text-xs h-8" onClick={() => navigate('/login')} data-testid="cs-signin-btn">
              Sign In for Full Access
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Mode Tabs */}
        <Tabs value={mode} onValueChange={(v) => { setMode(v); setResults(null); }} className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-gray-900 mb-1" data-testid="cs-page-title">Cost Savings Analysis</h1>
              <p className="text-sm text-gray-500">Enter your data to discover savings opportunities</p>
            </div>
            <TabsList className="bg-gray-100">
              <TabsTrigger value="part" data-testid="tab-part" className="data-[state=active]:bg-green-600 data-[state=active]:text-white text-xs">
                <Cpu className="w-3.5 h-3.5 mr-1.5" /> Part-Level
              </TabsTrigger>
              <TabsTrigger value="bom" data-testid="tab-bom" className="data-[state=active]:bg-[#0B3D91] data-[state=active]:text-white text-xs">
                <Layers className="w-3.5 h-3.5 mr-1.5" /> BOM-Level
              </TabsTrigger>
            </TabsList>
          </div>

          {/* ═══════ PART LEVEL ═══════ */}
          <TabsContent value="part" className="space-y-6 mt-0">
            {!results ? (
              <PartInputForm
                form={partForm}
                onChange={updatePartForm}
                onAnalyze={handleAnalyzePart}
                analyzing={analyzing}
                filteredMPNs={filteredMPNs}
                showSuggestions={showSuggestions}
                setShowSuggestions={setShowSuggestions}
                sampleParts={SAMPLE_PARTS}
                onSelectSample={handleSelectSample}
              />
            ) : (
              <PartResults results={results} onBack={() => setResults(null)} onAssign={handleAssign} />
            )}
          </TabsContent>

          {/* ═══════ BOM LEVEL ═══════ */}
          <TabsContent value="bom" className="space-y-6 mt-0">
            {!bomResults ? (
              <BOMInputForm
                file={bomFile}
                onFileChange={setBomFile}
                onAnalyze={handleAnalyzeBOM}
                onSampleBOM={handleSampleBOM}
                analyzing={analyzing}
              />
            ) : (
              <BOMResults components={bomResults} totalSavings={bomTotalSavings} onAssign={handleAssign} onReset={() => { setBomResults(null); setBomFile(null); }} />
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Assign Dialog */}
      <Dialog open={!!assignDialog} onOpenChange={() => setAssignDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base">Assign Savings Action</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs text-gray-600">Assign To</Label>
              <Input placeholder="team.member@company.com" className="mt-1" data-testid="assign-email-input" />
            </div>
            <div>
              <Label className="text-xs text-gray-600">Priority</Label>
              <Select defaultValue="high">
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="critical">Critical</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-gray-600">Note</Label>
              <Input placeholder="Optional note..." className="mt-1" data-testid="assign-note-input" />
            </div>
            <Button className="w-full bg-green-600 hover:bg-green-700 text-white" onClick={submitAssign} data-testid="assign-submit-btn">
              <Send className="w-4 h-4 mr-2" /> Assign Action
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   PART INPUT FORM
   ═══════════════════════════════════════════════════════════ */
const PartInputForm = ({ form, onChange, onAnalyze, analyzing, filteredMPNs, showSuggestions, setShowSuggestions, sampleParts, onSelectSample }) => (
  <Card className="border-gray-200 shadow-sm">
    <CardContent className="p-6">
      {/* Quick-Pick Sample MPNs */}
      <div className="mb-5 pb-5 border-b border-gray-100">
        <div className="flex items-center gap-2 mb-3">
          <Zap className="w-3.5 h-3.5 text-amber-500" />
          <span className="text-xs font-semibold text-gray-700">Quick Demo — Try a Sample Part</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {sampleParts.map((s) => (
            <button
              key={s.mpn}
              onClick={() => onSelectSample(s)}
              className={`px-3 py-1.5 rounded-lg border text-xs transition-all ${
                form.mpn === s.mpn
                  ? 'bg-green-50 border-green-300 text-green-800 font-semibold'
                  : 'bg-white border-gray-200 text-gray-600 hover:border-green-300 hover:bg-green-50/50'
              }`}
              data-testid={`sample-part-${s.mpn}`}
            >
              <span className="font-mono font-semibold">{s.mpn}</span>
              <span className="text-gray-400 ml-1.5">•</span>
              <span className="ml-1.5 text-gray-500">{s.label}</span>
              <span className="text-gray-400 ml-1.5">•</span>
              <span className="ml-1.5">${s.price}</span>
            </button>
          ))}
        </div>
      </div>
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Col 1 - Core */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            <Cpu className="w-4 h-4 text-green-600" /> Part Details
          </h3>
          <div className="relative">
            <Label className="text-xs text-gray-600">Manufacturer Part Number (MPN)</Label>
            <Input
              value={form.mpn}
              onChange={(e) => { onChange('mpn', e.target.value); setShowSuggestions(true); }}
              onFocus={() => setShowSuggestions(true)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
              placeholder="e.g. STM32F407VGT6"
              className="mt-1"
              data-testid="input-mpn"
            />
            {showSuggestions && filteredMPNs.length > 0 && (
              <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto" data-testid="mpn-suggestions">
                {filteredMPNs.map((m) => (
                  <button
                    key={m.mpn}
                    className="w-full text-left px-3 py-2 hover:bg-green-50 transition-colors border-b border-gray-50 last:border-0"
                    onMouseDown={() => {
                      onChange('mpn', m.mpn);
                      onChange('currentPrice', String(m.spotPrice));
                      setShowSuggestions(false);
                    }}
                    data-testid={`suggestion-${m.mpn}`}
                  >
                    <div className="text-xs font-mono font-semibold text-gray-900">{m.mpn}</div>
                    <div className="text-[10px] text-gray-500">{m.manufacturer} — ${m.spotPrice} spot</div>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div>
            <Label className="text-xs text-gray-600">Your Current Price ($)</Label>
            <Input type="number" step="0.01" value={form.currentPrice} onChange={(e) => onChange('currentPrice', e.target.value)} placeholder="e.g. 8.50" className="mt-1" data-testid="input-price" />
          </div>
          <div>
            <Label className="text-xs text-gray-600">Quantity</Label>
            <Input type="number" value={form.quantity} onChange={(e) => onChange('quantity', e.target.value)} placeholder="1000" className="mt-1" data-testid="input-qty" />
          </div>
          <div>
            <Label className="text-xs text-gray-600">Incoterms</Label>
            <Select value={form.incoterms} onValueChange={(v) => onChange('incoterms', v)}>
              <SelectTrigger className="mt-1" data-testid="input-incoterms"><SelectValue /></SelectTrigger>
              <SelectContent>
                {['EXW', 'FCA', 'FOB', 'CIF', 'DDP', 'DAP'].map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Col 2 - Supply Chain */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            <Globe className="w-4 h-4 text-[#0B3D91]" /> Supply Constraints
          </h3>
          <div>
            <Label className="text-xs text-gray-600">Distributor Type</Label>
            <Select value={form.distributorType} onValueChange={(v) => onChange('distributorType', v)}>
              <SelectTrigger className="mt-1" data-testid="input-dist-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="authorized">Authorized Only</SelectItem>
                <SelectItem value="independent">Independent</SelectItem>
                <SelectItem value="both">Both</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-gray-600">Lead Time (weeks)</Label>
            <Input type="number" value={form.leadTime} onChange={(e) => onChange('leadTime', e.target.value)} placeholder="8" className="mt-1" data-testid="input-lead-time" />
          </div>
          <div>
            <Label className="text-xs text-gray-600">Country of Origin Constraints</Label>
            <Input value={form.countryConstraints} onChange={(e) => onChange('countryConstraints', e.target.value)} placeholder="e.g. Exclude CN, RU" className="mt-1" data-testid="input-country" />
          </div>
          <div>
            <Label className="text-xs text-gray-600">Date Code Validity</Label>
            <Select value={form.dateCodeValidity} onValueChange={(v) => onChange('dateCodeValidity', v)}>
              <SelectTrigger className="mt-1" data-testid="input-date-code"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="1year">Within 1 Year</SelectItem>
                <SelectItem value="2years">Within 2 Years</SelectItem>
                <SelectItem value="3years">Within 3 Years</SelectItem>
                <SelectItem value="any">Any</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Col 3 - Alternates */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            <Target className="w-4 h-4 text-green-600" /> Alternate Preferences
          </h3>
          <div>
            <Label className="text-xs text-gray-600">Alternate Grade</Label>
            <Select value={form.alternateGrade} onValueChange={(v) => onChange('alternateGrade', v)}>
              <SelectTrigger className="mt-1" data-testid="input-alt-grade"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="form-fit-function">Form-Fit-Function (Drop-in)</SelectItem>
                <SelectItem value="parametric">Parametric Equivalent</SelectItem>
                <SelectItem value="functional">Functional Equivalent</SelectItem>
                <SelectItem value="any">Any Suitable Alternate</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="p-4 bg-green-50 rounded-lg border border-green-100 mt-4">
            <div className="flex items-start gap-2">
              <Shield className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs font-semibold text-green-800">Your Data is Protected</p>
                <p className="text-[10px] text-green-700 mt-1 leading-relaxed">
                  All data is encrypted, used only for your analysis, and never shared with third parties. SOC 2 Type II & ISO 27001 certified.
                </p>
              </div>
            </div>
          </div>
          <Button
            className="w-full bg-green-600 hover:bg-green-700 text-white h-11 mt-2"
            onClick={onAnalyze}
            disabled={analyzing}
            data-testid="analyze-part-btn"
          >
            {analyzing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyzing...</> : <><Zap className="w-4 h-4 mr-2" /> Analyze Cost Position</>}
          </Button>
        </div>
      </div>
    </CardContent>
  </Card>
);

/* ═══════════════════════════════════════════════════════════
   PART RESULTS
   ═══════════════════════════════════════════════════════════ */
const PartResults = ({ results, onBack, onAssign }) => {
  const { part, basePrice, qty, percentile, efficiency, marketMedian, lowestSpot,
    holdBuy, confidence, priceDistribution, prediction, spotRates, alternates,
    totalPartSavings, totalAltSavings, totalSavings } = results;

  const userPriceIndex = useMemo(() => {
    const idx = priceDistribution.findIndex((p) => p.price >= basePrice);
    return idx >= 0 ? idx : priceDistribution.length - 1;
  }, [priceDistribution, basePrice]);

  return (
    <div className="space-y-6">
      {/* Back + Summary Bar */}
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-900" data-testid="results-back-btn">
          <ArrowLeft className="w-4 h-4" /> New Analysis
        </button>
        <div className="flex items-center gap-2">
          <Badge className="bg-green-100 text-green-700 border-0 text-xs">{part.mpn}</Badge>
          <Badge className="bg-gray-100 text-gray-600 border-0 text-xs">{part.make}</Badge>
        </div>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {[
          { label: 'Part Efficiency', value: pct(efficiency), color: efficiency > 60 ? 'text-green-700' : 'text-amber-600', icon: Activity },
          { label: 'Your Percentile', value: `${percentile.toFixed(0)}th`, color: percentile < 50 ? 'text-green-700' : 'text-red-600', icon: BarChart3 },
          { label: 'Market Median', value: fmt(marketMedian), color: 'text-gray-900', icon: DollarSign },
          { label: 'Signal', value: holdBuy, color: holdBuy === 'BUY' ? 'text-green-700' : 'text-amber-600', icon: holdBuy === 'BUY' ? TrendingDown : Clock, sub: `${confidence.toFixed(0)}% conf.` },
          { label: 'Total Savings', value: fmt(totalSavings), color: 'text-green-700', icon: Zap, sub: `for ${qty.toLocaleString()} units` },
        ].map((kpi) => (
          <Card key={kpi.label} className="border-gray-200 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <kpi.icon className="w-4 h-4 text-gray-400" />
                <span className="text-xs text-gray-500">{kpi.label}</span>
              </div>
              <div className={`text-2xl font-bold ${kpi.color}`}>{kpi.value}</div>
              {kpi.sub && <div className="text-[10px] text-gray-400 mt-0.5">{kpi.sub}</div>}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts Row 1: Price Distribution + Prediction */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Price Range Distribution */}
        <Card className="border-gray-200 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-green-600" /> Price Range & Your Position
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="text-[10px] text-gray-500 mb-2">
              You are paying <span className="font-semibold text-gray-900">{fmt(basePrice)}</span> — at the <span className={`font-semibold ${percentile < 50 ? 'text-green-700' : 'text-red-600'}`}>{percentile.toFixed(0)}th percentile</span> of the market
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={priceDistribution} barCategoryGap="1%">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="label" tick={{ fontSize: 9 }} interval={3} />
                <YAxis tick={{ fontSize: 9 }} />
                <Tooltip
                  contentStyle={{ fontSize: 11 }}
                  formatter={(v) => v.toFixed(2)}
                />
                <ReferenceLine x={priceDistribution[userPriceIndex]?.label} stroke="#dc2626" strokeWidth={2} strokeDasharray="4 4" label={{ value: 'You', fill: '#dc2626', fontSize: 10 }} />
                <Bar dataKey="density" radius={[2, 2, 0, 0]}>
                  {priceDistribution.map((entry, i) => (
                    <Cell key={i} fill={i === userPriceIndex ? '#dc2626' : entry.price <= marketMedian ? '#16a34a' : '#94a3b8'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <div className="flex items-center gap-4 mt-2 text-[10px] text-gray-500">
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-green-600" /> Below Median</span>
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-gray-400" /> Above Median</span>
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-red-600" /> Your Price</span>
            </div>
          </CardContent>
        </Card>

        {/* Price Prediction */}
        <Card className="border-gray-200 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              <TrendingDown className="w-4 h-4 text-[#0B3D91]" /> Price Prediction
              <Badge className={`ml-auto text-[10px] border-0 ${holdBuy === 'BUY' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                {holdBuy} — {confidence.toFixed(0)}% confidence
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={prediction}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tick={{ fontSize: 9 }} />
                <YAxis tick={{ fontSize: 9 }} tickFormatter={(v) => `$${v.toFixed(2)}`} domain={['auto', 'auto']} />
                <Tooltip contentStyle={{ fontSize: 11 }} formatter={(v) => v ? `$${v.toFixed(3)}` : '-'} />
                <Area dataKey="upper" stroke="none" fill="#0B3D91" fillOpacity={0.08} />
                <Area dataKey="lower" stroke="none" fill="#0B3D91" fillOpacity={0.08} />
                <Line dataKey="actual" stroke="#16a34a" strokeWidth={2} dot={{ r: 2 }} />
                <Line dataKey="predicted" stroke="#0B3D91" strokeWidth={2} strokeDasharray="6 3" dot={{ r: 2 }} />
              </AreaChart>
            </ResponsiveContainer>
            <div className="flex items-center gap-4 mt-2 text-[10px] text-gray-500">
              <span className="flex items-center gap-1"><span className="w-4 h-0.5 bg-green-600" /> Historical</span>
              <span className="flex items-center gap-1"><span className="w-4 h-0.5 bg-[#0B3D91] border-dashed" style={{ borderTop: '2px dashed #0B3D91', height: 0 }} /> Predicted</span>
              <span className="flex items-center gap-1"><span className="w-4 h-2 bg-[#0B3D91]/10 rounded" /> Confidence Band</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 2: Spot Rate + Alternates */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Spot Rate vs Lead Time */}
        <Card className="border-gray-200 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              <Clock className="w-4 h-4 text-green-600" /> Lowest Spot Rate vs Lead Time & Quantity
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <ResponsiveContainer width="100%" height={220}>
              <ScatterChart margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="leadTime" name="Lead Time (wks)" tick={{ fontSize: 9 }} label={{ value: 'Lead Time (wks)', fontSize: 10, position: 'bottom', offset: -2 }} />
                <YAxis dataKey="price" name="Price" tick={{ fontSize: 9 }} tickFormatter={(v) => `$${v.toFixed(2)}`} />
                <ZAxis dataKey="size" range={[30, 200]} />
                <Tooltip
                  contentStyle={{ fontSize: 11 }}
                  formatter={(v, name) => name === 'Price' ? `$${v.toFixed(3)}` : v}
                />
                <Scatter data={spotRates} fill="#16a34a" fillOpacity={0.7} />
              </ScatterChart>
            </ResponsiveContainer>
            <div className="text-[10px] text-gray-400 mt-1">Bubble size = order quantity. Larger bubbles = higher volume discounts.</div>
          </CardContent>
        </Card>

        {/* Alternates Comparison */}
        <Card className="border-gray-200 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              <Target className="w-4 h-4 text-[#0B3D91]" /> Smart Alternates — Price Comparison
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50 text-xs">
                  <TableHead className="text-xs font-semibold">Alternate MPN</TableHead>
                  <TableHead className="text-xs font-semibold">Manufacturer</TableHead>
                  <TableHead className="text-xs font-semibold text-center">Match</TableHead>
                  <TableHead className="text-xs font-semibold text-right">Median Price</TableHead>
                  <TableHead className="text-xs font-semibold text-right">Savings vs Original</TableHead>
                  <TableHead className="text-xs font-semibold text-center">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {/* Original Part Row */}
                <TableRow className="bg-amber-50/50 text-xs">
                  <TableCell className="font-mono font-semibold text-gray-900">{part.mpn}</TableCell>
                  <TableCell className="text-gray-600">{part.make}</TableCell>
                  <TableCell className="text-center"><Badge className="bg-gray-100 text-gray-600 text-[10px] border-0">Original</Badge></TableCell>
                  <TableCell className="text-right font-semibold">{fmt(basePrice)}</TableCell>
                  <TableCell className="text-right text-gray-400">—</TableCell>
                  <TableCell />
                </TableRow>
                {alternates.map((alt) => (
                  <TableRow key={alt.mpn} className="text-xs hover:bg-gray-50">
                    <TableCell className="font-mono text-gray-900">{alt.mpn}</TableCell>
                    <TableCell className="text-gray-600">{alt.make}</TableCell>
                    <TableCell className="text-center">
                      <Badge className={`text-[10px] border-0 ${alt.match >= 90 ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                        {alt.match}%
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-semibold">{fmt(alt.medianPrice)}</TableCell>
                    <TableCell className="text-right">
                      {alt.savingsAbs > 0 ? (
                        <span className="text-green-700 font-semibold flex items-center justify-end gap-1">
                          <ArrowDownRight className="w-3 h-3" /> {fmt(alt.savingsAbs)} ({pct(alt.savingsPct)})
                        </span>
                      ) : (
                        <span className="text-red-600 font-semibold flex items-center justify-end gap-1">
                          <ArrowUpRight className="w-3 h-3" /> {fmt(Math.abs(alt.savingsAbs))}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <Button size="sm" variant="outline" className="h-6 text-[10px] border-gray-300" onClick={() => onAssign(alt)} data-testid={`assign-${alt.mpn}`}>
                        <Users className="w-3 h-3 mr-1" /> Assign
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      {/* Total Savings Summary */}
      <Card className="border-green-200 shadow-sm bg-green-50/30">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-1">Total Savings Potential</h3>
              <p className="text-xs text-gray-500">For {qty.toLocaleString()} units of {part.mpn}</p>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-green-700" data-testid="total-savings-value">{fmt(totalSavings)}</div>
              <div className="flex items-center gap-4 mt-1 text-[10px] text-gray-500">
                <span>Price optimization: {fmt(totalPartSavings)}</span>
                <span>Best alternate: {fmt(totalAltSavings)}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3 mt-4">
            <Button className="bg-green-600 hover:bg-green-700 text-white text-xs h-8" onClick={() => onAssign({ type: 'total', mpn: part.mpn })} data-testid="assign-total-btn">
              <Users className="w-3.5 h-3.5 mr-1.5" /> Assign to Team
            </Button>
            <Button variant="outline" className="border-gray-300 text-xs h-8" data-testid="download-report-btn">
              <FileText className="w-3.5 h-3.5 mr-1.5" /> Download Report
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   BOM INPUT FORM
   ═══════════════════════════════════════════════════════════ */
const BOMInputForm = ({ file, onFileChange, onAnalyze, onSampleBOM, analyzing }) => (
  <Card className="border-gray-200 shadow-sm">
    <CardContent className="p-8">
      <div className="max-w-2xl mx-auto text-center">
        <div className="w-16 h-16 bg-[#0B3D91]/10 rounded-2xl flex items-center justify-center mx-auto mb-5">
          <Layers className="w-8 h-8 text-[#0B3D91]" />
        </div>
        <h3 className="text-lg font-bold text-gray-900 mb-2">Upload Your Bill of Materials</h3>
        <p className="text-sm text-gray-500 mb-6">Upload a CSV/XLSX with columns: MPN, Quantity, Current Price. We'll analyze every component for savings.</p>

        <label className="block border-2 border-dashed border-gray-300 rounded-xl p-8 cursor-pointer hover:border-green-400 transition-colors mb-4" data-testid="bom-upload-area">
          <input
            type="file"
            accept=".csv,.xlsx"
            className="hidden"
            onChange={(e) => onFileChange(e.target.files?.[0] || null)}
            data-testid="bom-file-input"
          />
          {file ? (
            <div className="flex items-center justify-center gap-2 text-green-700">
              <CheckCircle className="w-5 h-5" />
              <span className="font-semibold">{file.name}</span>
            </div>
          ) : (
            <div className="text-gray-400">
              <Upload className="w-8 h-8 mx-auto mb-2" />
              <span className="text-sm">Drop your BOM file here or click to browse</span>
            </div>
          )}
        </label>

        <div className="flex items-center gap-3 justify-center mb-4">
          <Button
            className="bg-[#0B3D91] hover:bg-[#0B3D91]/90 text-white h-11 px-8"
            onClick={onAnalyze}
            disabled={analyzing || !file}
            data-testid="analyze-bom-btn"
          >
            {analyzing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyzing BOM...</> : <><Zap className="w-4 h-4 mr-2" /> Analyze BOM for Savings</>}
          </Button>
        </div>

        {/* Sample BOM Divider */}
        <div className="flex items-center gap-3 my-6">
          <div className="flex-1 h-px bg-gray-200" />
          <span className="text-xs text-gray-400 font-medium">OR</span>
          <div className="flex-1 h-px bg-gray-200" />
        </div>

        {/* Sample BOM Section */}
        <div className="bg-amber-50/70 border border-amber-200 rounded-xl p-5" data-testid="sample-bom-section">
          <div className="flex items-center justify-center gap-2 mb-3">
            <Zap className="w-4 h-4 text-amber-600" />
            <span className="text-sm font-semibold text-amber-800">Try a Sample BOM (10 Components)</span>
          </div>
          <div className="text-xs text-amber-700/80 mb-4 max-w-md mx-auto">
            Instantly analyze a real-world IoT gateway BOM featuring MCUs, power ICs, passives, and interface components — no upload needed.
          </div>
          <div className="flex flex-wrap justify-center gap-1.5 mb-4">
            {['STM32F407VGT6', 'ESP32-WROOM-32E', 'TPS54331DR', 'GRM188R71C104KA01D', 'BAT54S,215', 'USB3320C-EZK-TR', 'LAN8720A-CP-TR', 'LM358P', 'MAX232CPE', 'RC0402FR-071KL'].map((mpn) => (
              <Badge key={mpn} className="bg-white/80 text-amber-800 border border-amber-200 text-[9px] font-mono">{mpn}</Badge>
            ))}
          </div>
          <Button
            onClick={onSampleBOM}
            disabled={analyzing}
            className="bg-amber-600 hover:bg-amber-700 text-white h-10 px-6"
            data-testid="try-sample-bom-btn"
          >
            {analyzing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyzing 10 parts...</> : <><BarChart3 className="w-4 h-4 mr-2" /> Analyze Sample BOM</>}
          </Button>
        </div>

        <div className="flex items-center justify-center gap-2 mt-5 text-[10px] text-gray-400">
          <Shield className="w-3 h-3" /> Your data is encrypted and never shared
        </div>
      </div>
    </CardContent>
  </Card>
);

/* ═══════════════════════════════════════════════════════════
   BOM RESULTS
   ═══════════════════════════════════════════════════════════ */
const BOMResults = ({ components, totalSavings, onAssign, onReset }) => (
  <div className="space-y-6">
    {/* Back Button */}
    <div className="flex items-center justify-between">
      <button onClick={onReset} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-900" data-testid="bom-results-back-btn">
        <ArrowLeft className="w-4 h-4" /> New Analysis
      </button>
      <Badge className="bg-[#0B3D91]/10 text-[#0B3D91] border-0 text-xs">{components.length} Components</Badge>
    </div>

    {/* BOM KPIs */}
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {[
        { label: 'Total BOM Savings', value: fmt(totalSavings), color: 'text-green-700', icon: DollarSign },
        { label: 'Components Analyzed', value: components.length, color: 'text-gray-900', icon: Package },
        { label: 'Buy Signals', value: components.filter((c) => c.holdBuy === 'BUY').length, color: 'text-green-700', icon: TrendingDown },
        { label: 'Avg Efficiency', value: pct(components.reduce((s, c) => s + (100 - c.percentile + 15), 0) / components.length), color: 'text-gray-900', icon: Activity },
      ].map((kpi) => (
        <Card key={kpi.label} className="border-gray-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <kpi.icon className="w-4 h-4 text-gray-400" />
              <span className="text-xs text-gray-500">{kpi.label}</span>
            </div>
            <div className={`text-2xl font-bold ${kpi.color}`}>{kpi.value}</div>
          </CardContent>
        </Card>
      ))}
    </div>

    {/* Components Table */}
    <Card className="border-gray-200 shadow-sm">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold text-gray-900">Component-Level Cost Analysis</CardTitle>
      </CardHeader>
      <CardContent className="p-4">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50 text-xs">
              <TableHead className="text-xs font-semibold">MPN</TableHead>
              <TableHead className="text-xs font-semibold">Make</TableHead>
              <TableHead className="text-xs font-semibold text-right">Your Price</TableHead>
              <TableHead className="text-xs font-semibold text-right">Market Median</TableHead>
              <TableHead className="text-xs font-semibold text-center">Percentile</TableHead>
              <TableHead className="text-xs font-semibold text-center">Signal</TableHead>
              <TableHead className="text-xs font-semibold text-right">Qty</TableHead>
              <TableHead className="text-xs font-semibold text-right">Total Savings</TableHead>
              <TableHead className="text-xs font-semibold text-center">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {components.map((c) => (
              <TableRow key={c.mpn} className="text-xs hover:bg-gray-50">
                <TableCell className="font-mono font-semibold text-gray-900">{c.mpn}</TableCell>
                <TableCell className="text-gray-600">{c.make}</TableCell>
                <TableCell className="text-right">{fmt(c.basePrice)}</TableCell>
                <TableCell className="text-right text-green-700 font-semibold">{fmt(c.marketMedian)}</TableCell>
                <TableCell className="text-center">
                  <Badge className={`text-[10px] border-0 ${c.percentile < 50 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                    {c.percentile.toFixed(0)}th
                  </Badge>
                </TableCell>
                <TableCell className="text-center">
                  <Badge className={`text-[10px] border-0 ${c.holdBuy === 'BUY' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                    {c.holdBuy} ({c.confidence.toFixed(0)}%)
                  </Badge>
                </TableCell>
                <TableCell className="text-right">{c.qty.toLocaleString()}</TableCell>
                <TableCell className="text-right font-semibold text-green-700">{fmt(c.savings)}</TableCell>
                <TableCell className="text-center">
                  <Button size="sm" variant="outline" className="h-6 text-[10px] border-gray-300" onClick={() => onAssign(c)} data-testid={`bom-assign-${c.mpn}`}>
                    <Users className="w-3 h-3 mr-1" /> Assign
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>

    {/* BOM Total Savings */}
    <Card className="border-green-200 shadow-sm bg-green-50/30">
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-1">Total BOM Savings Potential</h3>
            <p className="text-xs text-gray-500">{components.length} components analyzed with volume-adjusted pricing</p>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold text-green-700" data-testid="bom-total-savings">{fmt(totalSavings)}</div>
          </div>
        </div>
        <div className="flex items-center gap-3 mt-4">
          <Button className="bg-green-600 hover:bg-green-700 text-white text-xs h-8" onClick={() => onAssign({ type: 'bom-total' })} data-testid="bom-assign-total-btn">
            <Users className="w-3.5 h-3.5 mr-1.5" /> Assign All to Team
          </Button>
          <Button variant="outline" className="border-gray-300 text-xs h-8" data-testid="bom-download-report-btn">
            <FileText className="w-3.5 h-3.5 mr-1.5" /> Download Full Report
          </Button>
        </div>
      </CardContent>
    </Card>
  </div>
);

export default CostSavingsHub;
